import { Component, computed, DestroyRef, inject, LOCALE_ID, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';

import { displayName, optionalName } from '../../../../validators/name-rules';
import { arabicOnlyValidator, englishOnlyValidator } from '../city.validators';

import { CityError, Governorate } from '../../../../pages/admin-dashboard/cities/city.model';
import { CityService } from '../../../../services/city/city.service';
import { SCOPES } from '../../../../services/scope/scope-constants';
import { HasScopeDirective } from '../../../../services/scope/has-scope.directive';
import { SubmitButton } from '../../../../shared/ui/Buttons/submit-button/submit-button';
import { ConfirmationDialog } from '../../../../shared/ui/confirmation-dialog/confirmation-dialog';
import { DropdownArrow } from '../../../../shared/ui/icons/dropdown-arrow/dropdown-arrow';
import { FilterInput } from '../../../../shared/ui/shared-filters/filter-input/filter-input';
import { Loading } from '../../../../utils/loading/loading';
import { SuccessfulNotification } from '../../../../utils/successful-notification/successful-notification';

@Component({
  selector: 'app-update-city-component',
  standalone: true,
  imports: [
    FilterInput,
    ReactiveFormsModule,
    ConfirmationDialog,
    SuccessfulNotification,
    Loading,
    SubmitButton,
    HasScopeDirective,
    DropdownArrow,
  ],
  templateUrl: './update-city-component.html',
  styleUrl: './update-city-component.scss',
})
export class UpdateCityComponent implements OnInit {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private locale = inject(LOCALE_ID);
  private cityService = inject(CityService);
  private destroyRef = inject(DestroyRef);

  readonly SCOPES = SCOPES;

  isArabic = computed(() => this.locale.startsWith('ar'));
  isLoading = signal(false);
  isFetchingDetails = signal(false);
  fetchDetailsError = signal('');

  governorates = signal<Governorate[]>([]);

  titleArLabel = $localize`:@@cityTitleArabic:City Name (Arabic)`;
  titleArPlaceholder = $localize`:@@cityTitleArabicPlaceholder:Enter city name in Arabic`;

  titleEnLabel = $localize`:@@cityTitleEnglish:City Name (English)`;
  titleEnPlaceholder = $localize`:@@cityTitleEnglishPlaceholder:Enter city name in English (optional)`;

  governorateLabel = $localize`:@@governorateLabel:Governorate`;
  governoratePlaceholder = $localize`:@@governoratePlaceholder:Select Governorate`;

  cancelButton = $localize`:@@cityCancel:Cancel`;
  submitButton = $localize`:@@cityUpdateSubmit:Save`;
  loadingText = $localize`:@@cityLoading:Loading...`;

  showConfirmation = signal(false);
  confirmationTitle = $localize`:@@cityUpdateConfirmationTitle:Confirm Update`;
  confirmationDescription = $localize`:@@cityUpdateConfirmation:Are you sure you want to update this city?`;
  confirmButtonText = signal($localize`:@@cityConfirm:Confirm`);
  cityExistsMessage = $localize`:@@cityExistsMessage:exists in`;

  cityForm = new FormGroup({
    titleAr: new FormControl<string | null>(null, [
      Validators.required,
      Validators.minLength(2),
      Validators.maxLength(100),
      arabicOnlyValidator(),
    ]),

    titleEn: new FormControl('', [
      Validators.minLength(2),
      Validators.maxLength(100),
      englishOnlyValidator(),
    ]),

    governorateId: new FormControl<number | null>(null, [Validators.required]),
  });

  cityId = signal<number | null>(null);
  titleArValue = signal<string | null>(null);
  titleEnValue = signal('');
  governorateIdValue = signal<number | null>(null);

  ngOnInit() {
    this.loadGovernorates();
    const state = history.state;
    this.cityId.set(state.id ?? null);
    this.titleArValue.set(state.titleAr ?? null);
    this.titleEnValue.set(state.titleEn ?? '');
    this.governorateIdValue.set(state.governorateId ?? null);

    this.cityForm.patchValue({
      titleAr: this.titleArValue(),
      titleEn: this.titleEnValue(),
      governorateId: this.governorateIdValue(),
    });
  }

  governoratesLoading = signal(false);
  governoratesError = signal('');

  loadGovernorates() {
    this.governoratesLoading.set(true);
    this.governoratesError.set('');

    const sub = this.cityService.getGovernorates().subscribe({
      next: (res) => {
        this.governorates.set(res.list || []);
        this.governoratesLoading.set(false);
      },
      error: (error: CityError) => {
        this.governoratesLoading.set(false);
        this.governoratesError.set(
          this.isArabic()
            ? error?.messageAr || 'حدث خطأ أثناء تحميل المحافظات'
            : error?.messageEn || 'An error occurred while loading governorates',
        );
      },
    });

    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  readonly titleArControl = computed(() => this.cityForm.controls.titleAr);
  readonly titleEnControl = computed(() => this.cityForm.controls.titleEn);
  readonly governorateIdControl = computed(() => this.cityForm.controls.governorateId);

  onArabicChange(value: string | number | null) {
    this.titleArControl().setValue(value?.toString() ?? null);
    this.titleArControl().markAsTouched();
    this.titleArControl().updateValueAndValidity();
  }

  onEnglishChange(value: string | number | null) {
    this.titleEnControl().setValue(value?.toString() ?? '');
    this.titleEnControl().markAsTouched();
    this.titleEnControl().updateValueAndValidity();
  }

  submitForm() {
    this.errorMessage = signal('');
    this.cityForm.markAllAsTouched();
    if (this.cityForm.invalid) {
      return;
    }
    this.showConfirmation.set(true);
  }

  private goToCitiesList(governorateId: number | null) {
    this.router.navigate(governorateId ? ['/admin/cities', governorateId] : ['/admin/governorates']);
  }

  cancel() {
    this.goToCitiesList(this.governorateIdValue());
  }

  successMessage = signal<string | null>(null);
  errorMessage = signal('');

  confirmUpdate() {
    this.isLoading.set(true);
    this.confirmButtonText.set(this.loadingText);
    this.errorMessage = signal('');

    const cityId = this.cityId();
    const data = this.cityForm.getRawValue();

    const dto = {
      titleAr: data.titleAr!.trim(),
      titleEn: optionalName(data.titleEn),
      governorateId: Number(data.governorateId),
    };

    const selectedGovernorate = this.governorates().find((gov) => gov.id === dto.governorateId);

    const governorateName = selectedGovernorate
      ? this.isArabic()
        ? selectedGovernorate.titleAr
        : selectedGovernorate.titleEn
      : '';

    const subscription = this.cityService.updateCity(cityId!, dto).subscribe({
      next: () => {
        this.isLoading.set(false);
        this.confirmButtonText.set($localize`:@@cityConfirm:Confirm`);
        this.showConfirmation.set(false);
        this.errorMessage.set('');

        const cityName = displayName(this.isArabic(), dto.titleAr, dto.titleEn);

        this.successMessage.set(
          `${$localize`:@@cityUpdatedSuccessfully:City Updated `} "${cityName}" ${$localize`:@@cityUpdatedSuccessfully2:successfully`}`,
        );

        setTimeout(() => {
          this.goToCitiesList(dto.governorateId);
        }, 3000);
      },

      error: (error: CityError) => {
        this.isLoading.set(false);
        this.confirmButtonText.set($localize`:@@cityConfirm:تأكيد`);

        if (error.reqBodyErrors) {
          const hasArabicError = error.reqBodyErrors.some((err) => err.includes('Field: titleAr'));
          const hasEnglishError = error.reqBodyErrors.some((err) => err.includes('Field: titleEn'));

          if (hasArabicError && hasEnglishError) {
            this.errorMessage.set(
              this.isArabic()
                ? 'اسم المدينة باللغة العربية والإنجليزية غير صالح.'
                : 'The Arabic and English city names are invalid.',
            );
            return;
          }

          if (hasArabicError) {
            this.errorMessage.set(
              this.isArabic() ? 'اسم المدينة باللغة العربية غير صالح.' : 'The Arabic city name is invalid.',
            );
            return;
          }

          if (hasEnglishError) {
            this.errorMessage.set(
              this.isArabic() ? 'اسم المدينة باللغة الإنجليزية غير صالح.' : 'The English city name is invalid.',
            );
            return;
          }
        }

        if (error.code === 'CITY-0001') {
          const cityName = displayName(this.isArabic(), dto.titleAr, dto.titleEn);

          this.errorMessage.set(`"${cityName}" ${this.cityExistsMessage} ${governorateName}`);
          return;
        }

        this.errorMessage.set(
          this.isArabic()
            ? error.messageAr || error.messageEn || 'حدث خطأ غير متوقع'
            : error.messageEn || error.messageAr || 'An unexpected error occurred',
        );
      },
    });

    this.destroyRef.onDestroy(() => subscription.unsubscribe());
  }

  closeConfirmation() {
    this.showConfirmation.set(false);
  }
}
